import { Schemas } from '~/clients/api-client'
import { DepartmentKey } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { Department, usePractitionerStatusDefinition } from '~/storm'

export type PractitionerAssignment = Schemas['PractitionerAssignment']
export type PractitionerStatusDefinition = Schemas['PractitionerStatusDefinitionOut']
export type TimeOfDay = Schemas['TimeOfDay']

export function isEveningPoli(definition: Pick<PractitionerStatusDefinition, 'assignment' | 'time_of_day'>): boolean {
    return definition.assignment === 'policlinic' && definition.time_of_day === 'evening'
}

export const assignmentToLabel: Record<PractitionerAssignment, string> = {
    surgery: 'Operasjon',
    policlinic: 'Poliklinikk',
    on_call_surgery: 'Ox',
    other_work_assignment: 'Andre arbeidsoppgaver',
    absence: 'Fravær',
    other: 'Annet',
} as const

const UNKNOWN_LABEL = 'Utilgjengelig'

export const isAssignment = (assignment: string): assignment is PractitionerAssignment => assignment in assignmentToLabel
export const mapAssignmentToLabel = (assignment: string) => (isAssignment(assignment) ? assignmentToLabel[assignment] : UNKNOWN_LABEL)

const assignmentOrder: Record<PractitionerAssignment, number> = {
    surgery: 0,
    policlinic: 1,
    absence: 2,
    on_call_surgery: 3,
    other_work_assignment: 4,
    other: 5,
}

export const getAssignmentOrder = (assignment: PractitionerAssignment) => assignmentOrder[assignment]

const timeOfDayOrder: Record<TimeOfDay, number> = {
    day_time: 1,
    evening: 2,
}

export const getTimeOfDayOrder = (timeOfDay: TimeOfDay | null | undefined): number => (timeOfDay ? timeOfDayOrder[timeOfDay] : 1)

export function sortStatusDefinitions<T extends Pick<PractitionerStatusDefinition, 'assignment' | 'order'>>(definitions: T[]): T[] {
    return definitions.toSorted((a, b) => {
        const orderA = getAssignmentOrder(a.assignment)
        const orderB = getAssignmentOrder(b.assignment)
        if (orderA !== orderB) return orderA - orderB
        return a.order - b.order
    })
}

type GroupedStatusDefinition = {
    assignment: PractitionerAssignment
    title: string
    statusCodes: string[]
}

/**
 * Group status definitions by their assignment, label each group, and sort groups + items within.
 */
export function groupStatusDefinitionsByAssignment(statusDefinitions: PractitionerStatusDefinition[]): GroupedStatusDefinition[] {
    const grouped = Object.groupBy(statusDefinitions, definition => definition.assignment)

    return Object.entries(grouped)
        .map(([assignment, definitions]) => ({
            assignment: assignment as PractitionerAssignment,
            title: mapAssignmentToLabel(assignment),
            statusCodes: definitions.toSorted((a, b) => a.order - b.order).map(d => d.code),
        }))
        .toSorted((a, b) => getAssignmentOrder(a.assignment) - getAssignmentOrder(b.assignment))
}

export function isDepartmentIncluded(departmentKey: DepartmentKey, departments: readonly Department[]) {
    return departmentKey === 'all' || departments.some(d => d.id === Number(departmentKey))
}

export function useIsStatusPartOfDepartment(status: string): boolean {
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const definition = usePractitionerStatusDefinition({ where: { code: status }, include: { departments: true } })
    return isDepartmentIncluded(departmentKey, definition?.departments ?? [])
}
